import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'antd';

import { verify2FA } from 'features/verify2FA/actions';
import {
	getUserProfile,
	getUserProfit,
	getUserProfitSecondaryMarket,
	saveRiskTolerance,
	acceptAgreement,
} from './actions';
import { register, saveContactInfo } from '../registration/actions';
import { login2FA, login, logout } from '../authorization/actions';
import { deleteAccountConfirm } from '../userSettings/actions';
import { deleteCounteroffer } from '../secondaryMarket/secondaryMarketSlice';

import { IUser, IAreaChartItem, RequestStatusTypes } from 'models';

import { apiReducerBuilder } from 'utils';
import i18n from 'utils/i18n';

interface UserState {
	profit: IAreaChartItem[];
	entity: IUser;
	status: RequestStatusTypes;
	timeUserLoaded: number;
}

const userSlice = createSlice({
	name: 'user',
	initialState: {
		entity: {},
		profit: [] as IAreaChartItem[],
	} as UserState,
	reducers: {},
	extraReducers: (builder) => {
		apiReducerBuilder(builder, getUserProfile, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.entity = payload;
			state.timeUserLoaded = new Date().getTime();
		});

		apiReducerBuilder(builder, saveRiskTolerance, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.entity = payload;
			state.timeUserLoaded = new Date().getTime();

			message.success(i18n.t('messageResultSavedText') as string);
		});
		apiReducerBuilder(builder, acceptAgreement, (state, action) => {
			const { payload, meta } = action;

			state.status = meta.requestStatus;
			state.entity = payload;
			state.timeUserLoaded = new Date().getTime();
		});

		apiReducerBuilder(builder, getUserProfit, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.profit = payload;
		});

		apiReducerBuilder(builder, getUserProfitSecondaryMarket, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.profit = payload;
		});

		builder.addCase(deleteCounteroffer, (state) => {
			state.entity.secondaryMarket.counterOffers.count =
				state.entity.secondaryMarket.counterOffers.count - 1;
		});

		builder.addMatcher(isAnyOf(logout, deleteAccountConfirm.fulfilled), (state) => {
			state.entity = {} as IUser;
			state.profit = [];
		});

		builder.addMatcher(isAnyOf(saveContactInfo.fulfilled, register.fulfilled), (state, action) => {
			const { payload } = action;

			state.entity = { ...state.entity, ...payload };
		});

		builder.addMatcher(isAnyOf(verify2FA.fulfilled), (state, action) => {
			const { payload } = action;

			state.entity = { ...state.entity, tfaActive: payload.tfa_is_active };
		});
		builder.addMatcher(isAnyOf(login.fulfilled, login2FA.fulfilled), (state, action) => {
			const { payload } = action;

			state.entity = { ...state.entity, status: payload.status };
		});
	},
});

export default userSlice.reducer;
